// components/reusables/ProductConfigDisplay/ProductConfigDisplay.tsx

"use client"
import React, { useEffect } from "react"
import styles from "./ProductConfigDisplay.module.css"
import { getFeaturesByProductType } from "../../../services/features/features"

const ProductConfigDisplay = ({ productType, userSelection }) => {
    console.log("Product Type:  ", productType)
    console.log("User Selection : ", userSelection)
    if (!userSelection || !productType) return null

    const featureGroups = getFeaturesByProductType(productType)

    // Helper to get display value from feature
    const getDisplayValue = (feature, value) => {
        if (value === undefined || value === null) return "-"

        // Handle options (select, radio)
        if (feature.options && feature.options.length > 0) {
            const option = feature.options.find(opt => opt.value === value)
            if (option) return option.label
        }

        // Handle boolean values
        if (typeof value === "boolean") {
            return value ? "Yes" : "No"
        }

        // Handle nested objects (like dimensions)
        if (typeof value === "object" && value !== null) {
            // Special formatting for dimensions
            if (feature.slug === "dimensions") {
                const { width, height } = value
                if (width && height) {
                    return `${width}" × ${height}"`
                }
            }
            // Special formatting for hardware
            if (feature.slug === "hardware") {
                const { hardwareType, motorizedType, vendor } = value
                if (hardwareType === "NONE") return "-"
                let display = hardwareType
                if (motorizedType && motorizedType !== "NONE") {
                    display += ` (${motorizedType})`
                }
                if (vendor) {
                    display += ` - ${vendor}`
                }
                return display
            }
            return JSON.stringify(value)
        }

        // Handle numbers and strings
        return String(value)
    }

    // Check if a feature should be displayed
    const shouldDisplayFeature = (feature, value) => {
        if (value === undefined || value === null) return false

        // Skip NONE or false values
        if (value === "NONE" || value === false) return false

        // Skip quantity if it's 1 (default)
        if (feature.slug === "quantity" && value === 1) return false

        return true
    }

    // Get value from userSelection based on feature
    const getFeatureValue = feature => {
        // Handle nested features with path
        if (feature.path) {
            const pathParts = feature.path.split(".")
            let value = userSelection
            for (const part of pathParts) {
                if (value && typeof value === "object") {
                    value = value[part]
                } else {
                    return undefined
                }
            }
            return value
        }

        // For dimensions, check if we have width/height at root level
        if (feature.slug === "dimensions") {
            if (
                userSelection.width !== undefined &&
                userSelection.height !== undefined
            ) {
                return { width: userSelection.width, height: userSelection.height }
            }
            return userSelection[feature.slug]
        }

        // For hardware, check if we have hardware at root level
        if (feature.slug === "hardware") {
            return userSelection[feature.slug]
        }

        // Handle regular slug
        return userSelection[feature.slug]
    }

    // Collect all key-value pairs
    const getConfigItems = () => {
        const items = []

        const processFeatures = features => {
            for (const feature of features) {
                const value = getFeatureValue(feature)

                // if (!shouldDisplayFeature(feature, value)) continue;

                // Handle nested features
                if (feature.type === "nested" && feature.features) {
                    // For dimensions
                    if (
                        feature.slug === "dimensions" &&
                        value &&
                        typeof value === "object"
                    ) {
                        const { width, height } = value
                        if (width && height) {
                            items.push({
                                label: feature.name,
                                value: `${width}" × ${height}"`
                            })
                        }
                    }
                    // For hardware, process its nested features
                    else if (
                        feature.slug === "hardware" &&
                        value &&
                        typeof value === "object"
                    ) {
                        processFeatures(feature.features)
                    }
                    // For other nested features
                    else {
                        processFeatures(feature.features)
                    }
                }
                // Handle regular features
                else {
                    const displayValue = getDisplayValue(feature, value)
                    if (displayValue) {
                        items.push({
                            label: feature.name,
                            value: displayValue
                        })
                    }
                }
            }
        }

        featureGroups.forEach(group => {
            processFeatures(group.features)
        })

        return items
    }

    const configItems = getConfigItems()

    if (configItems.length === 0) return null

    return (
        <div className={styles.configGrid}>
            {configItems.map((item, index) => (
                <div key={index} className={styles.configRow}>
                    <div className={styles.configLabel}>{item.label}</div>
                    <div className={styles.configValue}>{item.value}</div>
                </div>
            ))}
        </div>
    )
}

export default ProductConfigDisplay
